import { c as _c } from "react/compiler-runtime";
/** shadcn:button — upstream synced via `pnpm dlx shadcn add button --diff` */
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** Shared toggle-on styling for toolbar-style buttons */
const toggleOnPrimary = 'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground';

/** Genesys default / outline: idle → primary on active/on */
const genesysDefaultVariant = `bg-input-idle text-foreground hover:bg-input-hover hover:text-hover-foreground active:bg-primary active:text-primary-foreground ${toggleOnPrimary}`;

/** Title bar button: same as default but active uses active-foreground */
const genesysTitleBarVariant = `bg-input-idle text-foreground hover:bg-input-hover hover:text-hover-foreground active:bg-primary active:text-active-foreground ${toggleOnPrimary}`;
const transparentChrome = 'bg-transparent text-foreground hover:bg-input-idle hover:text-hover-foreground active:bg-input-hover active:text-active-foreground data-[state=on]:bg-transparent data-[state=on]:text-active-foreground';
const iconToggleChrome = 'hover:bg-transparent hover:text-hover-foreground active:bg-transparent active:text-active-foreground data-[state=on]:bg-transparent data-[state=on]:text-active-foreground';
const iconChrome = 'hover:bg-transparent hover:text-hover-foreground active:bg-transparent active:text-foreground data-[state=on]:bg-transparent data-[state=on]:text-primary';
const buttonVariants = cva(['inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none', 'font-normal text-foreground', 'focus-visible:border-ring focus-visible:!ring-1 focus-visible:!ring-ring/10', 'disabled:pointer-events-none disabled:opacity-50', 'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40', '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4'].join(' '), {
  variants: {
    variant: {
      default: genesysDefaultVariant,
      outline: genesysDefaultVariant,
      destructive: 'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
      secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
      ghost: 'bg-transparent text-foreground hover:bg-input-idle hover:text-hover-foreground active:bg-input-hover active:text-active-foreground data-[state=on]:bg-transparent data-[state=on]:text-active-foreground',
      link: 'text-primary underline-offset-4 hover:underline',
      clear: iconToggleChrome,
      destructiveHover: 'bg-input-idle text-foreground hover:bg-destructive hover:text-destructive-foreground active:bg-destructive active:text-destructive-foreground data-[state=on]:bg-destructive data-[state=on]:text-destructive-foreground',
      destructiveText: 'text-destructive/70 hover:bg-transparent hover:text-destructive',
      ghostDestructive: 'hover:bg-destructive hover:text-destructive-foreground dark:hover:bg-destructive',
      icon: iconChrome,
      overlay: 'border bg-input/30 border-input shadow-xs text-accent-foreground hover:text-primary-foreground hover:bg-accent/80',
      primary: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary',
      primaryHover: `bg-input-idle text-foreground hover:bg-primary hover:text-primary-foreground active:bg-primary active:text-primary-foreground ${toggleOnPrimary}`,
      select: 'border-input bg-transparent font-normal text-secondary-foreground data-placeholder:text-muted-foreground shadow-xs dark:bg-input/10 dark:hover:bg-input/20 justify-between',
      titleBar: genesysTitleBarVariant,
      transparent: transparentChrome,
      /** @deprecated Use `transparent` */
      toolbar: transparentChrome,
      /** @deprecated Use `clear` */
      iconToggle: iconToggleChrome
    },
    size: {
      default: 'h-7 px-2 text-sm',
      titleBar: 'h-7 px-2',
      xs: 'h-6 px-2 text-xs',
      sm: 'h-7 px-2',
      md: 'h-8 px-4',
      lg: 'h-10 px-6',
      icon: 'size-6',
      'icon-sm': 'size-8',
      'icon-lg': 'size-10',
      'icon-xs': 'size-6',
      symbol: 'size-6.5'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});

// @genesys-start variants block
/** @deprecated Use `buttonVariants` */
const genesysButtonVariants = buttonVariants;
// @genesys-end variants block

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;
const buttonClassName = (options?: VariantProps<typeof buttonVariants>, className?: string) => cn(buttonVariants(options), className);
type ButtonProps = React.ComponentProps<'button'> & VariantProps<typeof buttonVariants> & {
  asChild?: boolean;
  tooltip?: React.ReactNode;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  showTooltipWhenDisabled?: boolean;
};
const Button = t0 => {
  const $ = _c(29);
  let className;
  let props;
  let size;
  let t1;
  let t2;
  let tooltip;
  let tooltipSide;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      tooltip,
      tooltipSide,
      showTooltipWhenDisabled: t1,
      asChild: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
    $[4] = t1;
    $[5] = t2;
    $[6] = tooltip;
    $[7] = tooltipSide;
    $[8] = variant;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
    t1 = $[4];
    t2 = $[5];
    tooltip = $[6];
    tooltipSide = $[7];
    variant = $[8];
  }
  const showTooltipWhenDisabled = t1 === undefined ? true : t1;
  const asChild = t2 === undefined ? false : t2;
  const Comp = asChild ? Slot.Root : "button";
  let t3;
  if ($[9] !== className || $[10] !== size || $[11] !== variant) {
    t3 = buttonClassName({
      variant,
      size
    }, className);
    $[9] = className;
    $[10] = size;
    $[11] = variant;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== Comp || $[14] !== props || $[15] !== size || $[16] !== t3 || $[17] !== variant) {
    t4 = <Comp data-slot="button" data-variant={variant} data-size={size} className={t3} {...props} />;
    $[13] = Comp;
    $[14] = props;
    $[15] = size;
    $[16] = t3;
    $[17] = variant;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const buttonElement = t4;
  if (!tooltip) {
    return buttonElement;
  }
  let t5;
  if ($[19] !== buttonElement || $[20] !== props.disabled || $[21] !== showTooltipWhenDisabled) {
    t5 = <TooltipTrigger asChild={true}>{props.disabled && showTooltipWhenDisabled ? <div>{buttonElement}</div> : buttonElement}</TooltipTrigger>;
    $[19] = buttonElement;
    $[20] = props.disabled;
    $[21] = showTooltipWhenDisabled;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== tooltip || $[24] !== tooltipSide) {
    t6 = <TooltipContent className="whitespace-pre-line" side={tooltipSide}>{tooltip}</TooltipContent>;
    $[23] = tooltip;
    $[24] = tooltipSide;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t5 || $[27] !== t6) {
    t7 = <Tooltip>{t5}{t6}</Tooltip>;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
};
Button.displayName = 'Button';
export { Button, buttonVariants, genesysButtonVariants, buttonClassName, type ButtonVariant, type ButtonSize };